// Template deck — Product Launch.
//
// A generic, reusable "we are launching a product" narrative authored as a typed
// V2 SlideDeck. Populated with a single coherent fictional-but-believable
// product (Lumen — a field-service operations platform) so the template renders
// as a finished deck out of the box; swap the product specifics for a real
// launch. Arc: cover -> tension -> reframe -> reveal -> what it does -> show ->
// proof point -> supporting proof -> pricing -> availability -> early voice -> close.
//
// British English throughout, no emoji. Every kind/layout pair is valid per
// web/SLIDES.md, and every field name + value shape comes from slide-model.ts.
import type { SlideDeck } from '../slide-model';

export const productLaunchDeck: SlideDeck = {
  schemaVersion: 2,
  meta: {
    title: 'Lumen Launch',
    subtitle: 'Now available',
    author: 'Lumen',
    date: '2026',
    theme: 'workplace-paper',
    fontset: 'editorial',
    brand: '#2E5D4B',
  },
  slides: [
    {
      id: 'launch-01',
      kind: 'cover',
      label: '01 · Cover',
      coverStyle: 'editorial',
      eyebrow: 'Introducing',
      title: 'Lumen',
      sub: 'Field service operations, finally running in one place.',
      byline: 'Lumen · Operations Platform',
      foot: 'Launch · 2026',
    },
    {
      id: 'launch-02',
      kind: 'full-bleed',
      label: '02 · The problem',
      layout: 'overlay-bottom',
      art: 'ember',
      eyebrow: 'The problem',
      title: 'Field teams are flying blind.',
      lede: 'Today, service managers still chase jobs across spreadsheets, texts and three apps that do not talk to each other — slow, manual and disconnected.',
    },
    {
      id: 'launch-03',
      kind: 'quote',
      label: '03 · Why it matters',
      layout: 'centred',
      eyebrow: 'Why it matters',
      quote: 'Every hour an engineer spends working out where to go next is an hour the customer is still waiting.',
      name: 'Internal point of view',
      meta: 'From eighteen months of field-service research',
    },
    {
      id: 'launch-04',
      kind: 'split',
      label: '04 · The answer',
      layout: 'wide-media',
      side: 'right',
      art: 'ocean',
      eyebrow: 'The answer',
      title: 'Meet Lumen',
      lede: 'The operations platform that turns a backlog of jobs into a live, self-organising schedule your whole team can see.',
      items: [
        'One live view of every job, engineer and asset',
        'Routing and scheduling that adjust themselves in real time',
        'A field app that works the same offline as on',
      ],
      caption: 'Lumen — the field that runs itself.',
    },
    {
      id: 'launch-05',
      kind: 'features',
      label: '05 · What it does',
      layout: 'trio',
      eyebrow: 'What it does',
      title: 'Three things, done brilliantly',
      cards: [
        {
          icon: 'home',
          h: 'Plan',
          d: 'Drag jobs onto a live board, or let Lumen sequence the day for you around skills, parts and travel time.',
        },
        {
          icon: 'trending',
          h: 'Dispatch',
          d: 'Send the right engineer to the right job in one tap, and watch progress update on the map as it happens.',
        },
        {
          icon: 'shield',
          h: 'Prove',
          d: 'Capture photos, signatures and parts used on site, then turn every visit into an audit-ready record automatically.',
        },
      ],
    },
    {
      id: 'launch-06',
      kind: 'gallery',
      label: '06 · In the product',
      layout: 'grid-3',
      eyebrow: 'In the product',
      title: 'See it in action',
      items: [
        { art: 'mist', t: 'The board', n: 'A planner drags tomorrow into shape in minutes.' },
        { art: 'dawn', t: 'On the road', n: 'The engineer opens the next job, routed and ready.' },
        { art: 'sage', t: 'The proof', n: 'A signed, costed visit lands back in the office.' },
      ],
      footer: 'Replace the placeholder art with real product screenshots before sharing.',
    },
    {
      id: 'launch-07',
      kind: 'bignum',
      label: '07 · The proof point',
      layout: 'band',
      value: '3x',
      vlabel: 'more jobs completed per engineer, per day',
      delta: 'vs the old spreadsheet-and-phone way',
      down: false,
      lede: 'From early access: the single number our first cohort kept coming back to.',
    },
    {
      id: 'launch-08',
      kind: 'stat-row',
      label: '08 · Early results',
      layout: 'colour-band',
      title: 'Early results',
      lede: 'From the beta cohort, across their first ninety days.',
      stats: [
        { n: '94%', accent: '94%', label: 'would recommend Lumen to a peer' },
        { n: '3 wk', label: 'average time from sign-up to first live crew' },
        { n: '40+', label: 'organisations live in the private beta' },
      ],
    },
    {
      id: 'launch-09',
      kind: 'pricing',
      label: '09 · Pricing',
      layout: 'cards',
      eyebrow: 'Pricing',
      title: 'Pick your plan',
      lede: 'Simple, transparent tiers. Start free, scale when you are ready.',
      tiers: [
        {
          name: 'Starter',
          price: 'Free',
          period: '',
          note: 'For small crews finding their feet',
          tag: 'Free',
          featured: false,
          features: [
            'Live job board and dispatch',
            'Up to 5 engineers',
            'Mobile field app',
            'Community support',
          ],
        },
        {
          name: 'Team',
          price: '£24',
          period: '/user/mo',
          note: 'For growing service teams',
          tag: 'Popular',
          featured: true,
          features: [
            'Everything in Starter',
            'Automatic routing and scheduling',
            'Photo, signature and parts capture',
            'Priority support',
          ],
        },
        {
          name: 'Enterprise',
          price: 'Custom',
          period: '',
          note: 'For multi-region operations',
          tag: 'Contact us',
          featured: false,
          features: [
            'Everything in Team',
            'SSO, audit log and data residency',
            'Dedicated success manager',
            '99.9% uptime SLA',
          ],
        },
      ],
      footer: 'Annual billing available. No card required to start.',
    },
    {
      id: 'launch-10',
      kind: 'timeline',
      label: '10 · Availability',
      layout: 'milestones-band',
      eyebrow: 'Availability',
      title: 'Rolling out now',
      milestones: [
        {
          date: 'Today',
          t: 'General availability',
          n: 'Lumen is live for all customers across the UK and Ireland.',
          done: true,
          active: true,
        },
        {
          date: 'Jul 2026',
          t: 'European launch',
          n: 'Multi-language field app and EU data residency go live.',
          done: false,
        },
        {
          date: 'Q4 2026',
          t: 'Predictive maintenance',
          n: 'Sensor-driven jobs that schedule themselves before a failure.',
          done: false,
        },
      ],
    },
    {
      id: 'launch-11',
      kind: 'quote-image',
      label: '11 · Customer voice',
      layout: 'side-quote',
      art: 'plum',
      quote: 'We cleared a four-week backlog in nine days, and our engineers stopped phoning the office to ask what was next.',
      name: 'Priya Nandakumar',
      meta: 'Head of Service, Meridian Facilities',
    },
    {
      id: 'launch-12',
      kind: 'cta',
      label: '12 · Close',
      layout: 'with-image',
      art: 'dawn',
      eyebrow: 'Available today',
      headline: 'Try Lumen now.',
      sub: 'Free to start. No card required. Your first live crew in under three weeks.',
      contact: [
        ['Start', 'lumenops.com/start'],
        ['Docs', 'lumenops.com/docs'],
        ['Talk to us', 'sales@lumenops.com'],
      ],
    },
  ],
  notes: {
    'launch-01': 'Open warmly and slowly — the name first, the promise second. Do not rush into features.',
    'launch-07': 'This is the slide to land hard. Pause on 3x. Everything before sets it up; everything after supports it.',
    'launch-09': 'Anchor on Team — it is the plan most launch customers land on. Starter is the on-ramp, Enterprise is the upsell.',
  },
};
